import { Schema } from 'effect';
import { ChecklistSchema } from './checklist';

export const VehicleIdSchema = Schema.Number;
export type VehicleId = Schema.Schema.Type<typeof VehicleIdSchema>;

export const VehicleSchema = Schema.transform(
  Schema.Struct({
    id: VehicleIdSchema,
    name: Schema.String,
    registration_number: Schema.optional(Schema.NullOr(Schema.String)),
    center_id: Schema.Number,
    checklists: Schema.Array(Schema.encodedSchema(ChecklistSchema))
  }),
  Schema.Struct({
    id: VehicleIdSchema,
    name: Schema.String,
    registrationNumber: Schema.optional(Schema.NullOr(Schema.String)),
    centerId: Schema.Number,
    checklists: Schema.Array(ChecklistSchema)
  }),
  {
    strict: true,
    decode: (api) => ({
      id: api.id,
      name: api.name,
      registrationNumber: api.registration_number,
      centerId: api.center_id,
      checklists: api.checklists
    }),
    encode: (client) => ({
      id: client.id,
      name: client.name,
      registration_number: client.registrationNumber,
      center_id: client.centerId,
      checklists: client.checklists
    })
  }
);
export type Vehicle = Schema.Schema.Type<typeof VehicleSchema>;

export const VehicleStateNotificationSchema = Schema.transform(
  Schema.Struct({
    id: VehicleIdSchema,
    is_in_service: Schema.Boolean,
    is_parked: Schema.Boolean,
    is_connected: Schema.Boolean,
    simulation_status: Schema.optional(Schema.NullOr(Schema.String))
  }),
  Schema.Struct({
    id: VehicleIdSchema,
    isInService: Schema.Boolean,
    isParked: Schema.Boolean,
    isConnected: Schema.Boolean,
    simulationStatus: Schema.optional(Schema.NullOr(Schema.String))
  }),
  {
    strict: true,
    decode: (api) => ({
      id: api.id,
      isInService: api.is_in_service,
      isParked: api.is_parked,
      isConnected: api.is_connected,
      simulationStatus: api.simulation_status
    }),
    encode: (client) => ({
      id: client.id,
      is_in_service: client.isInService,
      is_parked: client.isParked,
      is_connected: client.isConnected,
      simulation_status: client.simulationStatus
    })
  }
);
export type VehicleStateNotification = Schema.Schema.Type<typeof VehicleStateNotificationSchema>;

export const BatteryChargingState = {
  CHARGING: 'charging',
  NOT_CHARGING: 'not_charging',
  ERROR: 'error',
  UNKNOWN: 'unknown'
} as const;

export const BatteryChargingStateSchema = Schema.Literal(
  BatteryChargingState.CHARGING,
  BatteryChargingState.NOT_CHARGING,
  BatteryChargingState.ERROR,
  BatteryChargingState.UNKNOWN
);

export type BatteryChargingState = Schema.Schema.Type<typeof BatteryChargingStateSchema>;

export const VehicleMissionComputerStatusNotificationSchema = Schema.transform(
  Schema.Struct({
    vehicle_id: VehicleIdSchema,
    error_description: Schema.optional(Schema.NullOr(Schema.String)),
    battery_charge_pct: Schema.optional(Schema.NullOr(Schema.Number)),
    battery_charging_state: BatteryChargingStateSchema
  }),
  Schema.Struct({
    vehicleId: VehicleIdSchema,
    errorDescription: Schema.optional(Schema.NullOr(Schema.String)),
    batteryChargePct: Schema.optional(Schema.NullOr(Schema.Number)),
    batteryChargingState: BatteryChargingStateSchema
  }),
  {
    strict: true,
    decode: (api) => ({
      vehicleId: api.vehicle_id,
      errorDescription: api.error_description,
      batteryChargePct: api.battery_charge_pct,
      batteryChargingState: api.battery_charging_state
    }),
    encode: (client) => ({
      vehicle_id: client.vehicleId,
      error_description: client.errorDescription,
      battery_charge_pct: client.batteryChargePct,
      battery_charging_state: client.batteryChargingState
    })
  }
);
export type VehicleMissionComputerStatusNotification = Schema.Schema.Type<
  typeof VehicleMissionComputerStatusNotificationSchema
>;

export const VehiclePreparationStatus = {
  UNPREPARED: 'unprepared',
  PENDING_PREPARATION: 'pending_prep',
  PREPARED: 'prepared'
} as const;

export const VehiclePreparationStatusSchema = Schema.Literal(
  VehiclePreparationStatus.UNPREPARED,
  VehiclePreparationStatus.PENDING_PREPARATION,
  VehiclePreparationStatus.PREPARED
);

export type VehiclePreparationStatus = Schema.Schema.Type<typeof VehiclePreparationStatusSchema>;

export const VehiclesPreparationStatusNotificationSchema = Schema.transform(
  Schema.Struct({
    status_by_vehicle_id: Schema.Record({
      key: Schema.String,
      value: VehiclePreparationStatusSchema
    })
  }),
  Schema.Struct({
    statusByVehicleId: Schema.Record({
      key: Schema.String,
      value: VehiclePreparationStatusSchema
    })
  }),
  {
    strict: true,
    decode: (api) => ({
      statusByVehicleId: api.status_by_vehicle_id
    }),
    encode: (client) => ({
      status_by_vehicle_id: client.statusByVehicleId
    })
  }
);
export type VehiclesPreparationStatusNotification = Schema.Schema.Type<
  typeof VehiclesPreparationStatusNotificationSchema
>;

export const SimpleVehicleRequestSchema = Schema.transform(
  Schema.Struct({
    vehicle_id: VehicleIdSchema
  }),
  Schema.Struct({
    vehicleId: VehicleIdSchema
  }),
  {
    decode: (api) => ({ vehicleId: api.vehicle_id }),
    encode: (client) => ({ vehicle_id: client.vehicleId })
  }
);
export type SimpleVehicleRequest = Schema.Schema.Type<typeof SimpleVehicleRequestSchema>;

export const VehicleModuleVersionSchema = Schema.transform(
  Schema.Struct({
    module: Schema.String,
    display_name: Schema.String,
    version: Schema.optional(Schema.NullOr(Schema.String)),
    last_update_timestamp: Schema.optional(Schema.NullOr(Schema.String))
  }),
  Schema.Struct({
    module: Schema.String,
    displayName: Schema.String,
    version: Schema.optional(Schema.NullOr(Schema.String)),
    lastUpdateTimestamp: Schema.optional(Schema.NullOr(Schema.String))
  }),
  {
    strict: true,
    decode: (api) => ({
      module: api.module,
      displayName: api.display_name,
      version: api.version,
      lastUpdateTimestamp: api.last_update_timestamp
    }),
    encode: (client) => ({
      module: client.module,
      display_name: client.displayName,
      version: client.version,
      last_update_timestamp: client.lastUpdateTimestamp
    })
  }
);
export type VehicleModuleVersion = Schema.Schema.Type<typeof VehicleModuleVersionSchema>;
